import { FilterNbAirportOption } from '@/src/types/flight'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'
import useClevertapForSrp from '../../analytics/clevertap/srp'
import useUpdateSearchResultCache from '../updateSearchResultCache'
import useSearchResults from '../useSearchResults'
export interface IncludeAirport extends FilterNbAirportOption {
    isSelected?: boolean
}

export interface NbAirportGroup {
    airportCode: string
    airportName: string
    groupType: 'departure' | 'arrival'
    airports: IncludeAirport[]
}

const useIncludeAirportsManager = (journeyLeg: 0 | 1 = 0) => {
    const t = useTranslations('srpFilters')
    const title = t('airports')
    const { triggerSrpEvent } = useClevertapForSrp()

    const updateCache = useUpdateSearchResultCache()

    const { filterData, jsons, selectedJourneyLeg } = useSearchResults(journeyLeg)
    const { nbAirportFilter: nbAirportFilters } = filterData || {}
    const nbAirportGroupsRaw = nbAirportFilters?.[selectedJourneyLeg] || []
    const airportsJson = jsons?.ap || {}

    const nbAirportGroups: NbAirportGroup[] = useMemo(() => {
        return nbAirportGroupsRaw.map((group, index) => {
            const resolvedCode = group.airportCode
            const cityInfo = airportsJson[resolvedCode]
            const originalAirport = airportsJson[resolvedCode]
            const airports: IncludeAirport[] = [
                {
                    label: originalAirport
                        ? `${originalAirport.n}, ${originalAirport.c}`
                        : resolvedCode,
                    value: resolvedCode,
                    subLabel: '',
                    isSelected: group.selectedValues?.includes(resolvedCode) || false
                },
                ...(group.options || []).map(option => {
                    const airport = airportsJson[option.value]
                    return {
                        label: airport ? `${airport.n}, ${airport.c}` : option.value,
                        value: option.value,
                        subLabel: option.subLabel,
                        isSelected: group.selectedValues?.includes(option.value) || false
                    }
                })
            ]

            return {
                airportCode: resolvedCode,
                airportName: cityInfo?.c || resolvedCode,
                groupType: index === 0 ? 'departure' : 'arrival',
                airports
            }
        })
    }, [nbAirportGroupsRaw, airportsJson])

    const handleSelectToggle = useCallback(
        (id: string) => {
            const groupIndex = nbAirportGroupsRaw.findIndex(
                g => g.airportCode === id || g.options.some(o => o.value === id)
            )
            if (groupIndex === -1) return

            const group = nbAirportGroupsRaw[groupIndex]
            const isRemoving = group.selectedValues?.includes(id)
            const allSelectedValues = nbAirportGroupsRaw.flatMap(g => g.selectedValues || [])
            const newAllSelected = isRemoving
                ? allSelectedValues.filter(s => s !== id)
                : [...allSelectedValues, id]

            triggerSrpEvent('Air_UI_Action', {
                action_name: 'filter',
                action_type: 'nearby_airports',
                action_value: newAllSelected.join(',') || 'none'
            })

            updateCache(currentData => {
                const targetGroup = currentData?.filterData?.nbAirportFilter?.[selectedJourneyLeg]?.[groupIndex]
                if (targetGroup) {
                    if (isRemoving) {
                        targetGroup.selectedValues = targetGroup.selectedValues.filter(s => s !== id)
                    } else {
                        targetGroup.selectedValues.push(id)
                    }
                }
                return currentData as NonNullable<typeof currentData>
            })
        },
        [nbAirportGroupsRaw, selectedJourneyLeg, triggerSrpEvent, updateCache]
    )

    const handleReset = useCallback(() => {
        triggerSrpEvent('Air_UI_Action', {
            action_name: 'filter',
            action_type: 'reset',
            action_value: 'nearby_airports'
        })

        updateCache(currentData => {
            const groups = currentData?.filterData?.nbAirportFilter?.[selectedJourneyLeg]
            if (groups) {
                groups.forEach(group => {
                    group.selectedValues = []
                })
            }
            return currentData as NonNullable<typeof currentData>
        })
    }, [selectedJourneyLeg, triggerSrpEvent, updateCache])

    const shouldShowReset = useMemo(() => {
        return nbAirportGroupsRaw.some(g => g.selectedValues?.length > 0)
    }, [nbAirportGroupsRaw])

    const hasAnyOptions = useMemo(() => {
        return nbAirportGroups.some(g => g.airports.length > 0)
    }, [nbAirportGroups])

    return {
        title,
        nbAirportGroups,
        handleSelectToggle,
        handleReset,
        shouldShowReset,
        hasAnyOptions
    }
}

export default useIncludeAirportsManager
